import { z, ZodIssueCode } from "zod";
import { billingStatusSchema } from "./billing";

// ─── Trip ────────────────────────────────────────────────────────────────────

export const tripStatusSchema = z.enum([
  "pendiente", "en_curso", "completado", "cancelado",
])

export const travelAdvanceSchema = z.enum(["viaje", "gnc", "viaje_gnc"]).nullable()
export type TravelAdvance = z.infer<typeof travelAdvanceSchema>

export const tripIdSchema = z.string().uuid("ID de viaje inválido")

export const createTripSchema = z.object({
  shipmentId:         z.string().uuid().nullable().optional(),
  shipmentFromStopId: z.string().uuid().nullable().optional(),
  shipmentToStopId:   z.string().uuid().nullable().optional(),
  mobileId:           z.string().uuid().nullable().optional(),
  driverId:          z.string().uuid().nullable().optional(),
  truckId:           z.string().uuid().nullable().optional(),
  trailerId:         z.string().uuid().nullable().optional(),
  originCityId:      z.string().uuid().nullable().optional(),
  destinationCityId: z.string().uuid().nullable().optional(),
  departureAt:       z.string().datetime({ offset: true }).nullable().optional(),
  arrivalAt:         z.string().datetime({ offset: true }).nullable().optional(),
  status:            tripStatusSchema.optional(),
  billingStatus:     billingStatusSchema.nullable().optional(),
  notes:             z.string().nullable().optional(),
  hasTravelAdvance:  travelAdvanceSchema.optional(),
})

export const updateTripSchema = createTripSchema.omit({ shipmentId: true }).partial()

export const splitTripSchema = z.object({
  cityId:    z.string().uuid("Ciudad de corte requerida"),
  mobileId:  z.string().uuid().nullable().optional(),
  driverId:  z.string().uuid().nullable().optional(),
  truckId:   z.string().uuid().nullable().optional(),
  trailerId: z.string().uuid().nullable().optional(),
})

export type SplitTrip = z.infer<typeof splitTripSchema>

export const tripQuerySchema = z.object({
  shipmentId:    z.string().uuid().optional(),
  mobileId:      z.string().uuid().optional(),
  deleted:       z.enum(["true", "false"]).optional(),
  status:        tripStatusSchema.optional(),
  billingStatus: z.enum(["pendiente", "liquidado", "facturado", "cerrado"]).optional(),
  driverName:    z.string().max(200).optional(),
  operatorName:  z.string().max(200).optional(),
  hasShipment:   z.enum(["true", "false"]).optional(),
  search:        z.string().max(200).optional(),
  dateFrom:      z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/).optional(),
  dateTo:        z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/).optional(),
  sortBy:        z.enum(["originArrival", "internalId"]).optional().default("originArrival"),
  sortDir:       z.enum(["asc", "desc"]).optional().default("desc"),
  page:          z.coerce.number().int().min(1).optional().default(1),
  pageSize:      z.coerce.number().int().min(1).max(500).optional().default(25),
})

export type TripStatus    = z.infer<typeof tripStatusSchema>
export type TripId        = z.infer<typeof tripIdSchema>
export type CreateTrip    = z.infer<typeof createTripSchema>
export type UpdateTrip    = z.infer<typeof updateTripSchema>
export type TripQuery     = z.infer<typeof tripQuerySchema>

// ─── ServiceTypes ────────────────────────────────────────────────────────────────

// ─── ServiceTypes ────────────────────────────────────────────────────────────────────
export const serviceTypeIdSchema = z.string().uuid("ID de tipo de servicio inválido");

export const createServiceType = z.object({
  name: z.string().min(1, "El nombre es requerido").max(100),
  code: z.string().min(1, "El código es requerido").max(10).toUpperCase(),
});

export const updateServiceType = createServiceType.partial();

export const serviceTypeQuerySchema = z.object({
  deleted: z.enum(["true", "false"]).optional(),
});


// >─── Export Service Type ────────────────────────────────────────────────────────────────────
export type ServiceTypeId = z.infer<typeof serviceTypeIdSchema>;
export type CreateServiceType = z.infer<typeof createServiceType>;
export type UpdateServiceType = z.infer<typeof updateServiceType>;
export type ServiceTypeQuery = z.infer<typeof serviceTypeQuerySchema>;
